import PropTypes from "prop-types";

import { useMapDrawer } from "@pageComponents/itineraries/mapDrawer/context";
import { PathIndicator } from "@pageComponents/itineraries/pathIndicator";
import { dangerHTML } from "@utils/danger";

import { Overview } from "./overview";
import { Detail } from "./detail";

export function Day({ day, dayIndex, setDaySectionRef }) {
  const { open, setOpen, setPois } = useMapDrawer();

  const handleMapInteraction = (e, esid) => {
    e.stopPropagation();

    if (open) {
      setOpen(false);
      return;
    }

    /**
     * Gather the day's pois and shift the current into the 0 position.
     * This aids the logic in map, which will destructure the first element
     * as the featured icon
     */
    const points = day.pois.map(poi => ({
      ...poi,
      ...(poi.esid === esid ? { primary: true } : {}),
    }));

    setPois(points);
    setOpen(true);
  };

  return (
    <section
      id={`d-${day.esid}`}
      data-i={dayIndex}
      ref={element => setDaySectionRef(element, dayIndex)}
    >
      {/* Day Intro */}
      <div className="pt-16 bg-itinerary-blue-50 md:pt-20">
        <div className="container lg:px-0 2xl:max-w-7xl">
          <header className="max-w-4xl pb-8 mx-auto text-center sm:pb-16">
            <span className="w-auto inline-block rounded-pill bg-itinerary-blue-200 px-4 py-1.5 text-blue font-bold">
              Day {dayIndex + 1}
            </span>
            <h2 className="my-4 text-3xl font-mencken md:text-5xl">
              {day.title}
            </h2>
            <div
              className="mt-4 text-lg text-center md:text-xl"
              {...dangerHTML(day.editorial)}
            />
          </header>

          <div className="h-88 md:h-[550px]">
            <Overview pois={day.pois} />
          </div>

          <PathIndicator />
        </div>
      </div>

      {/**
       * Begin poi section loop
       */}
      <ul className="mx-auto -mb-1 md:container md:mt-16 lg:px-0 2xl:max-w-7xl">
        {day.pois.map((poi, i) => (
          <li key={poi.esid} id={poi.esid}>
            <Detail
              {...poi}
              index={i}
              handleMapInteraction={handleMapInteraction}
              className={
                i !== day.pois.length - 1
                  ? "border-b border-blue-200 border-dashed"
                  : ""
              }
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

Day.propTypes = {
  day: PropTypes.shape({
    esid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    editorial: PropTypes.string.isRequired,
    pois: PropTypes.arrayOf(
      PropTypes.shape({
        esid: PropTypes.string,
        title: PropTypes.string,
        slug: PropTypes.string,
        editorial: PropTypes.string,
        image: PropTypes.shape({
          src: PropTypes.string,
          credit: PropTypes.string,
          alt: PropTypes.string,
        }),
        meta: PropTypes.shape({
          address: PropTypes.string,
          coords: PropTypes.shape({
            lat: PropTypes.number,
            lon: PropTypes.number,
          }),
          tags: PropTypes.arrayOf(
            PropTypes.shape({ slug: PropTypes.string.isRequired })
          ),
          parent: PropTypes.shape({
            slug: PropTypes.string.isRequired,
          }),
        }),
      })
    ).isRequired,
  }).isRequired,
  dayIndex: PropTypes.number.isRequired,
  setDaySectionRef: PropTypes.func.isRequired,
};
